import { create } from '@bufbuild/protobuf'
import {
    PaginationFilters,
    PaginationFiltersSchema,
    PaginationStatistics,
} from '@/generated/Messages/Common_pb'

/**
 * Iterator that loads all pages of the `loadData` function.
 * @param loadData Function that takes the filter and pagination information.
 * @param filter that is passed to all calls of `loadData`.
 *               This filter shouldn't contain a `pagination` property, as it will be overriden.
 * @param maxTakeItemCount Maximum number of items that should be taken on one single page.
 * @returns
 */
export async function* loadAllIter<
    TFilter extends { pagination?: PaginationFilters },
    TResponse extends {
        pagination?: PaginationStatistics
        data?: Record<string, any>[]
    },
>(
    loadData: (filter: TFilter) => Promise<TResponse> | undefined,
    filter: TFilter,
    maxTakeItemCount: number
): AsyncGenerator<NonNullable<TResponse['data']>, void, unknown> {
    if (filter.pagination) {
        console.warn(
            `Filters passed to loadAll query contains pagination information ${JSON.stringify(filter.pagination)}. They will be ignored.`
        )
    }

    // Load the first page
    const response = await loadData({
        ...filter,
        pagination: create(PaginationFiltersSchema, {
            skipItemCount: 0,
            takeItemCount: maxTakeItemCount,
        }),
    })

    yield response?.data ?? []

    // If no response or pagination information is returned, abort the generator.
    // Also abort if the total item count is the same as the length of the current page.
    // This leads to the end of the generator because, one page was enough to load all items.
    if (
        !response?.pagination ||
        !response.data ||
        response.pagination.totalItemCount === response.data.length
    ) {
        return
    }

    // Iterate over all pages that are missing, and load the data sequentally for it.
    for (
        let i = 1;
        i < Math.ceil(response.pagination.totalItemCount / maxTakeItemCount);
        i++
    ) {
        const response = await loadData({
            ...filter,
            pagination: create(PaginationFiltersSchema, {
                skipItemCount: maxTakeItemCount * i,
                takeItemCount: maxTakeItemCount,
            }),
        })

        yield response?.data ?? []
    }
}
